import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpTrayIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { toast } from 'react-hot-toast';
import { KnowledgeBaseDocument, KnowledgeSource, WebSource, WebSourceConfig } from '../../types';
import { validateCrawlUrl, webSourceApi } from '../../services/webSources';
import AddWebsiteModal, { DEFAULT_MAX_LINKS_PER_URL } from './AddWebsiteModal';
import KnowledgeModeCards from './KnowledgeModeCards';
import SourceRow from './SourceRow';
import CrawledPagesPanel from './CrawledPagesPanel';
import RejectionPanel from './RejectionPanel';
import { formatFileSize, formatRelative, toSources } from './format';
import { useUser } from '../../contexts/UserContext';

export const ACCEPTED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md'];
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

interface SyncStatus {
  status: string;
  jobId?: string;
  startTime?: string;
  endTime?: string;
  documentsCount: number;
  pendingDocuments: number;
  errorMessage?: string;
  canUpload: boolean;
}

interface KnowledgePanelProps {
  chatbotId?: string;
  documents: KnowledgeBaseDocument[];
  pendingDeletion: Set<string>;
  syncStatus: SyncStatus | null;
  isUploading: boolean;
  uploadProgress: Record<string, number>;
  knowledgeMode: 'strict' | 'augmented';
  onUploadFiles: (files: File[]) => void;
  onStageDelete: (documentId: string) => void;
  onUndoDelete: (documentId: string) => void;
  onDiscardChanges: () => void;
  onUpdateKnowledge: () => Promise<void> | void;
  onKnowledgeModeChange: (mode: 'strict' | 'augmented') => void;
}

type Filter = 'all' | 'files' | 'websites';
type Sort = 'recent' | 'name' | 'status';

const SORT_LABELS: Record<Sort, string> = {
  recent: 'Most recent',
  name: 'Name',
  status: 'Status',
};

/** Order used when sorting by status: things needing attention first. */
const STATUS_ORDER: Record<string, number> = {
  failed: 0,
  rejected: 1,
  not_indexed: 2,
  uploading: 3,
  indexing: 4,
  removing: 5,
  indexed: 6,
};

export default function KnowledgePanel({
  chatbotId,
  documents,
  pendingDeletion,
  syncStatus,
  isUploading,
  uploadProgress,
  knowledgeMode,
  onUploadFiles,
  onStageDelete,
  onUndoDelete,
  onDiscardChanges,
  onUpdateKnowledge,
  onKnowledgeModeChange,
}: KnowledgePanelProps) {
  const [websites, setWebsites] = useState<WebSource[]>([]);
  // Which website's page list is open, if any (C1).
  const [pagesFor, setPagesFor] = useState<KnowledgeSource | null>(null);
  // Which website's refusal is being explained, if any (F11).
  const [rejectionFor, setRejectionFor] = useState<KnowledgeSource | null>(null);
  const [pendingWebDeletion, setPendingWebDeletion] = useState<Set<string>>(new Set());
  const [showAddWebsite, setShowAddWebsite] = useState(false);
  // The empty state's inline URL field. Kept separate from the dialog's own
  // state: this covers the common case with connector defaults, the dialog
  // covers scope, filters and sitemaps.
  const { usage, refreshUsage } = useUser();
  const [quickUrl, setQuickUrl] = useState('');
  const [quickError, setQuickError] = useState<string | null>(null);
  const [addingQuick, setAddingQuick] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('recent');
  const [search, setSearch] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [committing, setCommitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Nested dragenter/dragleave fire per child element; count them so the zone
  // does not flicker as the pointer crosses rows.
  const dragDepth = useRef(0);

  const loadWebsites = useCallback(async () => {
    if (!chatbotId) return;
    try {
      const response = await webSourceApi.list(chatbotId);
      if (response.data) setWebsites(response.data.websites);
    } catch {
      // Endpoints are not deployed yet; an empty list is the correct fallback.
    }
  }, [chatbotId]);

  useEffect(() => {
    loadWebsites();
  }, [loadWebsites]);

  // Poll while anything is still in flight — which is NOT the same as `crawling`.
  //
  // `checkingSize` has to be in here, and leaving it out is the exact bug C13 was
  // fixed to prevent, one layer up. During the measurement window the backend status
  // is truthfully `indexed` (the previous copy is live and answering), so a condition
  // testing only `crawling` stops polling the moment the crawl finishes — and the tab
  // sits on "Checking size…" forever while the supervisor swaps underneath it. Seen
  // live 2026-08-20: the row was swapped and `indexed` in DynamoDB at 18:05:52 and the
  // open tab still showed Checking size, because nothing asked again.
  const anyInFlight = websites.some(site => site.status === 'crawling' || site.checkingSize);
  useEffect(() => {
    if (!anyInFlight) return;
    const timer = setInterval(loadWebsites, 5000);
    return () => clearInterval(timer);
  }, [anyInFlight, loadWebsites]);

  const allPending = useMemo(() => {
    const merged = new Set(pendingDeletion);
    pendingWebDeletion.forEach(id => merged.add(id));
    return merged;
  }, [pendingDeletion, pendingWebDeletion]);

  const sources = useMemo(
    () => toSources(documents, websites, allPending, uploadProgress),
    [documents, websites, allPending, uploadProgress]
  );

  // The filter only earns its place when both kinds are present — otherwise
  // every option returns the same list. When it is hidden, 'all' is the only
  // meaningful reading, whatever was selected last.
  const showFilter = sources.some(s => s.kind === 'file') && sources.some(s => s.kind === 'website');
  const effectiveFilter: Filter = showFilter ? filter : 'all';

  const visibleSources = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = sources.filter(source => {
      if (effectiveFilter === 'files' && source.kind !== 'file') return false;
      if (effectiveFilter === 'websites' && source.kind !== 'website') return false;
      if (!term) return true;
      return (
        source.name.toLowerCase().includes(term) ||
        (source.detail || '').toLowerCase().includes(term)
      );
    });

    return filtered.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'status') {
        const delta = (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9);
        return delta !== 0 ? delta : a.name.localeCompare(b.name);
      }
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime;
    });
  }, [sources, effectiveFilter, search, sort]);

  const isSyncing = syncStatus?.status === 'IN_PROGRESS' || syncStatus?.status === 'STARTING';
  const canUpload = !(syncStatus && !syncStatus.canUpload) && !isUploading && !!chatbotId;

  const addedCount =
    documents.filter(doc => doc.status === 'uploaded' && !pendingDeletion.has(doc.id)).length +
    websites.filter(site => site.status === 'pending' && !pendingWebDeletion.has(site.id)).length;
  const removedCount = allPending.size;
  const hasPendingChanges = addedCount > 0 || removedCount > 0;

  const indexedCount = sources.filter(s => s.status === 'indexed').length;
  const lastIndexedAt = useMemo(() => {
    const times = sources
      .filter(s => s.status === 'indexed' && s.updatedAt)
      .map(s => new Date(s.updatedAt as string).getTime())
      .filter(t => !Number.isNaN(t));
    return times.length ? new Date(Math.max(...times)).toISOString() : undefined;
  }, [sources]);

  // --- file intake ----------------------------------------------------------

  const acceptFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    if (!canUpload) {
      toast.error(isSyncing ? 'Wait for the current update to finish' : 'Uploads are unavailable right now');
      return;
    }
    onUploadFiles(Array.from(fileList));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (!e.dataTransfer.types.includes('Files')) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    acceptFiles(e.dataTransfer.files);
  };

  // --- websites -------------------------------------------------------------

  const handleAddWebsite = async (config: WebSourceConfig) => {
    if (!chatbotId) throw new Error('Save the chatbot first');
    const response = await webSourceApi.add(chatbotId, config);
    if (!response.success) throw new Error(response.error || 'Could not add this website');
    await loadWebsites();
    toast.success('Website added — press Update knowledge to read it');
  };

  /**
   * Add a website straight from the empty state, with the connector's defaults.
   *
   * People type "celsian.ai", not "https://celsian.ai", so a bare host is
   * normalised rather than rejected. https is prepended and never http: the
   * connector validates seedUrls against a regex that requires https, and an
   * http URL would be accepted here only to fail the data source
   * asynchronously. A URL typed as http:// still gets the explicit error.
   */
  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = quickUrl.trim();
    if (!raw) return;
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

    const invalid = validateCrawlUrl(url);
    if (invalid) {
      setQuickError(invalid);
      return;
    }

    setAddingQuick(true);
    try {
      await handleAddWebsite({ url, scope: 'section', maxLinksPerUrl: DEFAULT_MAX_LINKS_PER_URL });
      setQuickUrl('');
      setQuickError(null);
    } catch (err: any) {
      setQuickError(err?.message || 'Could not add this website');
    } finally {
      setAddingQuick(false);
    }
  };

  const handleRecrawl = async (source: KnowledgeSource) => {
    if (!chatbotId) return;
    try {
      // The service layer answers with `{ success: false, error }` rather than
      // throwing, so the catch below never sees a refused request — this used to
      // toast success on a 403 and leave the row exactly as it was. It matters
      // now that a refusal is something a customer can act on: the route declines
      // a refresh on the free plan, and declines one from an account already over
      // its storage limit, and both replies say what to do about it.
      const result = await webSourceApi.recrawl(chatbotId, source.id);
      if (!result.success) {
        toast.error(result.error || 'Could not start reading that site again');
        return;
      }
      await loadWebsites();
      toast.success(`Reading ${source.name} again`);
    } catch {
      toast.error('Could not start reading that site again');
    }
  };

  const handleStageDelete = (source: KnowledgeSource) => {
    if (source.kind === 'website') {
      setPendingWebDeletion(prev => new Set([...Array.from(prev), source.id]));
    } else {
      onStageDelete(source.id);
    }
  };

  const handleUndoDelete = (source: KnowledgeSource) => {
    if (source.kind === 'website') {
      setPendingWebDeletion(prev => {
        const next = new Set(prev);
        next.delete(source.id);
        return next;
      });
    } else {
      onUndoDelete(source.id);
    }
  };

  // Mirrors the parent's discard: staged removals go back, and websites added
  // but never read are dropped. Otherwise they would sit in the pending count
  // with no way to clear them.
  const handleDiscard = async () => {
    const neverRead = websites.filter(site => site.status === 'pending');
    setPendingWebDeletion(new Set());

    if (chatbotId && neverRead.length > 0) {
      await Promise.all(neverRead.map(site => webSourceApi.remove(chatbotId, site.id).catch(() => null)));
      await loadWebsites();
    }

    onDiscardChanges();
  };

  const handleCommit = async () => {
    if (!chatbotId || committing) return;
    setCommitting(true);
    try {
      // Websites first — they are independent data sources, so a failure here
      // must not stop the document sync from running.
      const toRemove = Array.from(pendingWebDeletion);
      const toStart = websites.filter(s => s.status === 'pending' && !pendingWebDeletion.has(s.id)).map(s => s.id);

      if (toRemove.length) {
        await Promise.all(toRemove.map(id => webSourceApi.remove(chatbotId, id).catch(() => null)));
        setPendingWebDeletion(new Set());
      }
      if (toStart.length) {
        await webSourceApi.startCrawl(chatbotId, toStart).catch(() => null);
      }
      if (toRemove.length || toStart.length) {
        await loadWebsites();
      }

      // Only sync the document data source when documents actually changed.
      // Websites are separate data sources, and startSync answers 400 when
      // there is nothing new to read — which would surface as a false error.
      const documentsChanged =
        documents.some(doc => doc.status === 'uploaded') || pendingDeletion.size > 0;
      if (documentsChanged) {
        await onUpdateKnowledge();
      }

      // Refresh the storage figures. Deletions and document uploads are
      // reflected in the ledger immediately, so this is accurate for them.
      // A crawl is not: its bytes are only known once the ingestion job
      // finishes and the reconciler measures it, so a site added here shows up
      // on a later visit rather than now. Better a figure that lags than one
      // that invents a number for content that has not been measured.
      refreshUsage();
    } finally {
      setCommitting(false);
    }
  };

  // --- render ---------------------------------------------------------------

  const hasAnySources = sources.length > 0;

  // This chatbot's share of the account's storage, appended to the line that
  // already carries state. Deliberately an AMOUNT and never a percentage: the
  // cap is per account, so "12 MB of 50 MB" on one bot would imply that bot has
  // its own 50 MB when in fact its siblings are eating the same allowance.
  const thisBotBytes = usage?.storage.byKnowledgeBase
    .find(kb => kb.chatbotId && kb.chatbotId === chatbotId)?.totalBytes;

  // C4: while a sync is running, the header REPLACES "last updated" with live status.
  // The status used to live only in the sticky bar at the bottom of the panel, sharing
  // it with the pending-change count and the Update button — so the thing the user is
  // waiting for competed with the action they had just taken, in the lowest-contrast
  // corner of the screen. This line already carries state and is where the eye goes.
  //
  // "last updated" is dropped rather than shown alongside, because during a sync it is
  // stale by definition and two timestamps invite the reader to trust the wrong one.
  const summary = !hasAnySources
    ? 'No sources yet'
    : `${sources.length} ${sources.length === 1 ? 'source' : 'sources'}` +
      (isSyncing
        ? ' · reading your sources…'
        : indexedCount > 0 && lastIndexedAt
          ? ` · last updated ${formatRelative(lastIndexedAt)}`
          : '') +
      (thisBotBytes ? ` · ${formatFileSize(thisBotBytes)}` : '');

  return (
    <div
      className="p-6 relative"
      onDragEnter={handleDragEnter}
      onDragOver={e => e.preventDefault()}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_EXTENSIONS.join(',')}
        className="sr-only"
        onChange={e => {
          acceptFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Knowledge</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{summary}</p>
        </div>

        {/* Two buttons rather than one "Add source" menu, and identical weight
            for both. A dropdown would teach parity once in the empty state and
            then hide websites behind a click forever, which is the
            discoverability problem this screen started with. Hidden while empty
            because the chooser below already offers both, more prominently. */}
        {hasAnySources && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={!canUpload}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <ArrowUpTrayIcon className="h-4 w-4 text-gray-400" />
              Upload files
            </button>
            <button
              onClick={() => setShowAddWebsite(true)}
              disabled={!chatbotId}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <GlobeAltIcon className="h-4 w-4 text-gray-400" />
              Add website
            </button>
          </div>
        )}
      </div>

      {/* Filter + search. Each control appears only once it can change something. */}
      {(showFilter || sources.length > 3) && (
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          {showFilter ? (
            <div className="inline-flex p-0.5 rounded-full bg-gray-100 dark:bg-slate-700/50">
              {(['all', 'files', 'websites'] as Filter[]).map(value => (
                <button
                  key={value}
                  onClick={() => setFilter(value)}
                  className={clsx(
                    'px-3.5 py-1.5 text-sm font-medium rounded-full transition-colors capitalize',
                    filter === value
                      ? 'bg-white dark:bg-slate-800 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
          ) : (
            <span />
          )}

          <div className="flex items-center gap-2">
            {sources.length > 8 && (
              <div className="relative">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search sources"
                  className="pl-9 pr-3 py-1.5 text-sm w-52 rounded-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
                />
              </div>
            )}
            {sources.length > 3 && (
              // pr-8 leaves room for the native caret, which otherwise sits on
              // top of the label.
              <select
                value={sort}
                onChange={e => setSort(e.target.value as Sort)}
                className="pl-3 pr-8 py-1.5 text-sm rounded-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white"
              >
                {(Object.keys(SORT_LABELS) as Sort[]).map(value => (
                  <option key={value} value={value}>
                    {SORT_LABELS[value]}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* Empty state: a one-time chooser offering both paths at equal weight.
          It exists ONLY while there is nothing here. Two columns are safe as a
          chooser but would be wrong as permanent structure — that is the
          Documents | Web Pages split this redesign removed, and the moment a
          source exists the single list takes over again.

          Splitting into two cards also sidesteps an interaction bug: a URL
          input cannot live inside a dropzone that is itself a button, because
          clicking the field would open the file picker. Here the left card is
          the button and the right card is a form. Border style carries the
          honest difference — dashed accepts drops, solid does not. */}
      {!hasAnySources && (
        <div className="mb-2">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 text-center mb-1">
            Give your chatbot something to read
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-5">
            Point it at your website, or upload documents — you can add both.
          </p>

          <div className="grid gap-4 sm:grid-cols-2">
            {/* Websites first: a new chatbot has no documents attached yet, so
                pasting a URL is the shortest path from empty to a bot that can
                actually answer something. */}
            <div
              className={clsx(
                'rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 px-6 py-8 flex flex-col items-center text-center',
                !chatbotId && 'opacity-50'
              )}
            >
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center mb-3">
                <GlobeAltIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Add a website</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                We read the pages on your site.
              </span>

              <form onSubmit={handleQuickAdd} className="w-full flex items-center gap-2">
                <input
                  type="text"
                  value={quickUrl}
                  onChange={e => {
                    setQuickUrl(e.target.value);
                    if (quickError) setQuickError(null);
                  }}
                  disabled={!chatbotId || addingQuick}
                  placeholder="yoursite.com"
                  className="flex-1 min-w-0 px-3 py-2 text-sm rounded-full border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={!chatbotId || addingQuick || !quickUrl.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors disabled:opacity-40 flex-shrink-0"
                >
                  {addingQuick ? 'Adding…' : 'Add'}
                </button>
              </form>

              {quickError ? (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2 text-left w-full">{quickError}</p>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowAddWebsite(true)}
                  disabled={!chatbotId}
                  className="text-xs text-gray-500 dark:text-gray-400 underline hover:no-underline mt-2.5 disabled:opacity-50"
                >
                  Crawl options
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => canUpload && fileInputRef.current?.click()}
              disabled={!canUpload}
              className={clsx(
                'rounded-xl border-2 border-dashed px-6 py-8 flex flex-col items-center text-center transition-colors',
                isDragging
                  ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-slate-700/50'
                  : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500',
                !canUpload && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-slate-700 flex items-center justify-center mb-3">
                <DocumentTextIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
              </div>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Upload documents</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                PDF, DOCX, TXT, MD · up to 10 MB each
              </span>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full">
                Choose files
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500 mt-2.5">
                or drop them anywhere on this panel
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Sources */}
      {hasAnySources && (
        <div className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {visibleSources.length === 0 ? (
            effectiveFilter === 'websites' ? (
              <div className="py-10 text-center">
                <GlobeAltIcon className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">No websites yet.</div>
                <button
                  onClick={() => setShowAddWebsite(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <GlobeAltIcon className="h-4 w-4" />
                  Add website
                </button>
              </div>
            ) : (
              <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                Nothing matches that filter.
              </div>
            )
          ) : (
            visibleSources.map(source => (
              <SourceRow
                key={source.id}
                source={source}
                indexingSince={syncStatus?.startTime}
                onStageDelete={handleStageDelete}
                onUndoDelete={handleUndoDelete}
                onRecrawl={handleRecrawl}
                onShowPages={setPagesFor}
                onShowRejection={setRejectionFor}
              />
            ))
          )}
        </div>
      )}

      {syncStatus?.status === 'FAILED' && (
        <div className="mt-4 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40">
          <div className="text-sm font-medium text-red-800 dark:text-red-300">Last update failed</div>
          <div className="text-sm text-red-700 dark:text-red-400 mt-0.5">
            {syncStatus.errorMessage || 'Something went wrong while reading your sources.'}
          </div>
          <button
            onClick={handleCommit}
            className="mt-2 text-sm font-medium text-red-800 dark:text-red-300 underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* C1: which pages this website actually contributed. */}
      {pagesFor && chatbotId && (
        <CrawledPagesPanel
          chatbotId={chatbotId}
          source={pagesFor}
          onClose={() => setPagesFor(null)}
        />
      )}

      {/* F11: why a crawl was refused, and what to do about it. */}
      {rejectionFor && (
        <RejectionPanel source={rejectionFor} onClose={() => setRejectionFor(null)} />
      )}

      {/* Answering behaviour */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-slate-700">
        <KnowledgeModeCards mode={knowledgeMode} onChange={onKnowledgeModeChange} disabled={!chatbotId} />
      </div>

      {/* Commit bar */}
      {(hasPendingChanges || isSyncing) && (
        <div className="sticky bottom-0 -mx-6 -mb-6 mt-6 px-6 py-3.5 bg-white/95 dark:bg-slate-800/95 backdrop-blur border-t border-gray-200 dark:border-slate-700 flex items-center justify-between gap-4">
          <div className="text-sm text-gray-600 dark:text-gray-300 min-w-0">
            {isSyncing ? (
              <span className="flex items-center gap-2">
                <span className="w-3.5 h-3.5 border-2 border-gray-300 border-t-gray-600 dark:border-slate-500 dark:border-t-white rounded-full animate-spin" />
                Reading your sources — this usually takes 1–3 minutes.
              </span>
            ) : (
              <>
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {addedCount + removedCount} pending {addedCount + removedCount === 1 ? 'change' : 'changes'}
                </span>
                <span className="text-gray-400 dark:text-gray-500">
                  {' — '}
                  {[
                    addedCount > 0 ? `${addedCount} added` : null,
                    removedCount > 0 ? `${removedCount} removed` : null,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </>
            )}
          </div>

          {!isSyncing && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleDiscard}
                disabled={committing}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600 rounded-full hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                Discard
              </button>
              <button
                onClick={handleCommit}
                disabled={committing}
                className="px-5 py-2 text-sm font-medium text-white bg-emerald-600 rounded-full hover:bg-emerald-700 transition-colors disabled:opacity-50"
              >
                {committing ? 'Starting…' : 'Update knowledge'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Full-panel drag overlay. This is the real drop target and always has
          been — the dashed box was only ever advertising it, which is why
          removing that box once sources exist costs no functionality.

          It also now carries the accepted formats. They used to live in the
          permanent dropzone; showing them here puts them in front of the user
          at the one moment they matter, while dropping. */}
      {isDragging && (
        <div className="absolute inset-3 rounded-2xl border-2 border-dashed border-gray-900 dark:border-white bg-white/85 dark:bg-slate-800/85 flex items-center justify-center pointer-events-none z-20">
          <div className="text-center">
            <CloudArrowUpIcon className="h-10 w-10 text-gray-900 dark:text-white mx-auto mb-2" />
            <div className="text-sm font-medium text-gray-900 dark:text-white">Drop to upload</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              PDF, DOCX, TXT, MD · up to 10 MB each
            </div>
          </div>
        </div>
      )}

      <AddWebsiteModal
        isOpen={showAddWebsite}
        onClose={() => setShowAddWebsite(false)}
        onAdd={handleAddWebsite}
      />
    </div>
  );
}
